import React, { useState, useRef } from 'react';
import {
  X, Search, Play, Square, ChevronRight, RotateCcw,
  Sparkles, ChevronUp, ChevronDown, Zap, Music, Activity,
  Layout, FileText, Check
} from 'lucide-react';
import { EARTH, TYPOGRAPHY } from '../theme/constants.js';
import { generateFullGuidance } from '../lib/ProductionGuidance.js';
import { generateStandaloneTrackAnalysis, generateRecreationGuide, findCuratedMatch } from '../lib/ReferenceLibrary.js';

function TrackAnalyzerDialog({ isOpen, onClose, onGenerate }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [recreationGuide, setRecreationGuide] = useState(null);
  const [showGuide, setShowGuide] = useState(false);
  const [playingTrackId, setPlayingTrackId] = useState(null);
  const searchTimeout = useRef(null);
  const audioRef = useRef(null);

  const togglePreview = (e, track) => {
    e.stopPropagation();
    if (!track.previewUrl) return;
    if (playingTrackId === track.id) {
      audioRef.current?.pause();
      setPlayingTrackId(null);
      return;
    }
    if (audioRef.current) { audioRef.current.pause(); }
    const audio = new Audio(track.previewUrl);
    audio.volume = 0.5;
    audio.onended = () => setPlayingTrackId(null);
    audio.play().catch(() => {});
    audioRef.current = audio;
    setPlayingTrackId(track.id);
  };

  const stopPreview = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setPlayingTrackId(null);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    setSelectedTrack(null);
    setAnalysisResult(null);
    setRecreationGuide(null);
    setShowGuide(false);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!query || query.trim().length < 2) { setSearchResults([]); setIsSearching(false); return; }
    setIsSearching(true);
    searchTimeout.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search-tracks?q=${encodeURIComponent(query.trim())}&limit=10`);
        if (!res.ok) throw new Error('Search failed');
        const data = await res.json();
        setSearchResults(data.results || []);
      } catch (err) {
        console.error('Track search error:', err.message);
        setSearchResults([]);
      }
      setIsSearching(false);
    }, 400);
  };

  const handleSelectTrack = (track) => {
    stopPreview();
    setSelectedTrack(track);
    setIsAnalyzing(true);
    try {
      const result = generateStandaloneTrackAnalysis(track);
      setAnalysisResult(result);
      const guide = generateRecreationGuide(result, track);
      setRecreationGuide(guide);
    } catch (err) {
      console.error('Track analysis error:', err.message);
    }
    setIsAnalyzing(false);
  };

  const handleApply = () => {
    if (!analysisResult) return;
    const guidance = generateFullGuidance(analysisResult, { loopElements: ['kick', 'snare', 'bass', 'synth', 'hi-hats'] });
    const output = {
      genre: analysisResult.genre,
      tempo: analysisResult.bpm,
      energyCurve: 'dynamic',
      sections: analysisResult.sections.map(s => s.label),
      bars: analysisResult.sections.map(s => s.bars),
      totalBars: analysisResult.totalBars,
      estimatedDuration: `${Math.floor(analysisResult.totalDuration / 60)}:${String(Math.round(analysisResult.totalDuration % 60)).padStart(2, '0')}`,
      _richSections: analysisResult.sections,
      _guidance: guidance,
    };
    onGenerate(output);
    onClose();
    // Reset state
    setSearchQuery('');
    setSearchResults([]);
    setSelectedTrack(null);
    setAnalysisResult(null);
  };

  const handleClose = () => {
    stopPreview();
    onClose();
    setSearchQuery('');
    setSearchResults([]);
    setSelectedTrack(null);
    setAnalysisResult(null);
    setRecreationGuide(null);
    setShowGuide(false);
  };

  if (!isOpen) return null;

  const formatDuration = (ms) => {
    if (!ms) return '';
    const min = Math.floor(ms / 60000);
    const sec = Math.round((ms % 60000) / 1000);
    return `${min}:${String(sec).padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ backgroundColor: 'rgba(28, 25, 23, 0.4)' }}>
      <div className="relative w-full max-w-2xl mx-4 rounded-2xl shadow-2xl overflow-hidden" style={{ backgroundColor: '#faf8f5', border: '1px solid rgba(214, 211, 209, 0.3)', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div className="px-6 py-5 flex items-center justify-between" style={{ backgroundColor: EARTH.olive[600], color: '#fff' }}>
          <div>
            <h2 className="text-lg font-bold" style={{ fontFamily: TYPOGRAPHY.display, letterSpacing: '0.5px', textTransform: 'uppercase' }}>Analyze a Track</h2>
            <p className="text-xs opacity-75">Search for any song — we'll reverse engineer its structure</p>
          </div>
          <button onClick={handleClose} className="p-1 rounded-full hover:bg-white/20 transition-colors"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4" style={{ minHeight: 0 }}>
          {/* Search input */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: EARTH.stone[400] }} />
            <input
              type="text"
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              placeholder='Search any song — try "Adam F Circles" or "Daft Punk"'
              className="w-full pl-10 pr-4 py-3.5 rounded-xl border text-sm"
              style={{ borderColor: EARTH.stone[200], backgroundColor: 'rgba(255,255,255,0.8)', fontFamily: TYPOGRAPHY.body }}
              autoFocus
            />
            {isSearching && <div className="absolute right-3 top-1/2 -translate-y-1/2"><div className="w-4 h-4 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: EARTH.olive[400], borderTopColor: 'transparent' }} /></div>}
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && !analysisResult && (
            <div className="space-y-2 mb-4">
              {searchResults.map(track => {
                const curated = findCuratedMatch(track.artist, track.title);
                return (
                  <button
                    key={track.id}
                    onClick={() => handleSelectTrack(track)}
                    className="w-full flex items-center gap-3 p-3.5 rounded-lg border transition-all hover:shadow-md text-left"
                    style={{
                      borderColor: selectedTrack?.id === track.id ? EARTH.olive[400] : EARTH.stone[200],
                      backgroundColor: selectedTrack?.id === track.id ? EARTH.olive[50] : '#fff',
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <div className="relative flex-shrink-0 w-12 h-12">
                      {track.artwork && <img src={track.artwork} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                      {track.previewUrl && (
                        <button onClick={(e) => togglePreview(e, track)} className="absolute inset-0 flex items-center justify-center rounded-lg transition-all" style={{ backgroundColor: playingTrackId === track.id ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.3)' }}>
                          {playingTrackId === track.id
                            ? <Square className="w-4 h-4 fill-white" style={{ color: '#fff' }} />
                            : <Play className="w-4 h-4 fill-white" style={{ color: '#fff' }} />}
                        </button>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm truncate" style={{ color: EARTH.stone[800] }}>{track.title}</div>
                      <div className="text-xs truncate" style={{ color: EARTH.stone[500] }}>{track.artist} {track.year ? `(${track.year})` : ''}</div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: EARTH.stone[100], color: EARTH.stone[600] }}>{track.genre}</span>
                        {track.durationMs && <span className="text-xs" style={{ color: EARTH.stone[400] }}>{formatDuration(track.durationMs)}</span>}
                        {curated && <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ backgroundColor: EARTH.olive[100], color: EARTH.olive[700] }}>EXACT STRUCTURE</span>}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 flex-shrink-0" style={{ color: EARTH.stone[300] }} />
                  </button>
                );
              })}
            </div>
          )}

          {/* No results */}
          {searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && !analysisResult && (
            <div className="text-center py-8" style={{ color: EARTH.stone[400] }}>
              <p className="text-sm">No results found for "{searchQuery}"</p>
              <p className="text-xs mt-1">Try searching for artist name or song title</p>
            </div>
          )}

          {/* Analysis Result */}
          {analysisResult && selectedTrack && (
            <div>
              {/* Track header */}
              <div className="flex items-center gap-4 p-5 rounded-xl mb-4" style={{ backgroundColor: EARTH.olive[50], border: `1px solid ${EARTH.olive[200]}` }}>
                <div className="relative flex-shrink-0 w-16 h-16">
                  {selectedTrack.artwork && <img src={selectedTrack.artwork} alt="" className="w-16 h-16 rounded-xl shadow-lg" />}
                  {selectedTrack.previewUrl && (
                    <button onClick={(e) => togglePreview(e, selectedTrack)} className="absolute inset-0 flex items-center justify-center rounded-xl transition-all" style={{ backgroundColor: playingTrackId === selectedTrack.id ? 'rgba(0,0,0,0.6)' : 'rgba(0,0,0,0.25)' }}>
                      {playingTrackId === selectedTrack.id
                        ? <Square className="w-5 h-5 fill-white" style={{ color: '#fff' }} />
                        : <Play className="w-5 h-5 fill-white" style={{ color: '#fff' }} />}
                    </button>
                  )}
                </div>
                <div className="flex-1">
                  <div className="font-bold text-base" style={{ color: EARTH.stone[800], fontFamily: TYPOGRAPHY.display }}>{selectedTrack.title}</div>
                  <div className="text-sm" style={{ color: EARTH.stone[600] }}>{selectedTrack.artist}</div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={{ backgroundColor: EARTH.olive[100], color: EARTH.olive[700] }}>{analysisResult.genre}</span>
                    <span className="text-xs" style={{ color: EARTH.stone[500] }}>{analysisResult.bpm} BPM</span>
                    {analysisResult.referenceTrack?.originalKey && <span className="text-xs" style={{ color: EARTH.stone[500] }}>{analysisResult.referenceTrack.originalKey}</span>}
                    {analysisResult.curatedMatch && <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: EARTH.terracotta[100], color: EARTH.terracotta[700] }}>EXACT MATCH</span>}
                  </div>
                </div>
                <button onClick={() => { setAnalysisResult(null); setSelectedTrack(null); }} className="p-2 rounded-lg hover:bg-white/50 transition-colors" title="Search again"><RotateCcw className="w-4 h-4" style={{ color: EARTH.stone[400] }} /></button>
              </div>

              {/* Description */}
              <p className="text-xs mb-4 px-1" style={{ color: EARTH.stone[500] }}>{analysisResult.templateDescription}</p>

              {/* Producer Insights (curated only) */}
              {analysisResult.referenceTrack?.insights && (
                <div className="p-3 rounded-xl mb-4" style={{ backgroundColor: EARTH.sand[50], border: `1px solid ${EARTH.sand[200]}` }}>
                  <div className="text-xs font-bold mb-2" style={{ color: EARTH.sand[700], fontFamily: TYPOGRAPHY.display, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Producer Insights</div>
                  {analysisResult.referenceTrack.insights.map((insight, i) => (
                    <p key={i} className="text-xs mb-1" style={{ color: EARTH.stone[600] }}>• {insight}</p>
                  ))}
                </div>
              )}

              {/* Section list */}
              <div className="text-xs font-bold mb-2" style={{ color: EARTH.stone[700], fontFamily: TYPOGRAPHY.display, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Structure — {analysisResult.totalBars} bars ({Math.floor(analysisResult.totalDuration / 60)}:{String(Math.round(analysisResult.totalDuration % 60)).padStart(2, '0')})
              </div>
              <div className="space-y-1.5 mb-4">
                {analysisResult.sections.map((section, i) => (
                  <div key={i} className="flex items-center gap-2 p-2.5 rounded-lg" style={{ backgroundColor: '#fff', border: `1px solid ${EARTH.stone[150] || EARTH.stone[200]}` }}>
                    {/* Energy bar */}
                    <div className="w-20 h-2 rounded-full overflow-hidden flex-shrink-0" style={{ backgroundColor: EARTH.stone[100] }}>
                      <div className="h-full rounded-full" style={{
                        width: `${section.energy * 100}%`,
                        backgroundColor: section.energy >= 0.8 ? EARTH.terracotta[500] : section.energy >= 0.5 ? EARTH.olive[500] : EARTH.sage[400],
                      }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-xs font-semibold" style={{ color: EARTH.stone[800] }}>{section.label}</span>
                      <span className="text-xs ml-2" style={{ color: EARTH.stone[400] }}>{section.bars} bars • {Math.round(section.energy * 100)}%</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Recreation Guide */}
              {recreationGuide && (
                <div className="mb-4">
                  <button
                    onClick={() => setShowGuide(!showGuide)}
                    className="w-full flex items-center justify-between p-3 rounded-xl transition-all"
                    style={{ background: `linear-gradient(135deg, ${EARTH.forest[600]}, ${EARTH.olive[700]})`, color: '#fff', boxShadow: '0 2px 12px rgba(52, 77, 62, 0.2)' }}
                  >
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      <span className="text-xs font-bold" style={{ fontFamily: TYPOGRAPHY.display, letterSpacing: '0.5px', textTransform: 'uppercase' }}>
                        How to Make a Track Like This
                      </span>
                    </div>
                    {showGuide ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>

                  {showGuide && (
                    <div className="mt-2 space-y-3">
                      {/* Workflow */}
                      <div className="p-3 rounded-xl" style={{ backgroundColor: EARTH.olive[50], border: `1px solid ${EARTH.olive[200]}` }}>
                        <div className="text-xs font-bold mb-2 flex items-center gap-1.5" style={{ color: EARTH.olive[700], fontFamily: TYPOGRAPHY.display, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          <Zap className="w-3 h-3" /> DAW Workflow
                        </div>
                        {recreationGuide.workflow.map((tip, i) => (
                          <div key={i} className="flex items-start gap-2 mb-1.5">
                            <span className="text-xs font-bold flex-shrink-0 w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: EARTH.olive[200], color: EARTH.olive[700], fontSize: '9px' }}>{i + 1}</span>
                            <p className="text-xs" style={{ color: EARTH.stone[600] }}>{tip}</p>
                          </div>
                        ))}
                      </div>

                      {/* Sound Design */}
                      <div className="p-3 rounded-xl" style={{ backgroundColor: EARTH.sand[50], border: `1px solid ${EARTH.sand[200]}` }}>
                        <div className="text-xs font-bold mb-2 flex items-center gap-1.5" style={{ color: EARTH.sand[700], fontFamily: TYPOGRAPHY.display, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          <Music className="w-3 h-3" /> Sound Design
                        </div>
                        {recreationGuide.soundDesign.map((tip, i) => (
                          <p key={i} className="text-xs mb-1.5" style={{ color: EARTH.stone[600] }}>• {tip}</p>
                        ))}
                      </div>

                      {/* Mix Techniques */}
                      <div className="p-3 rounded-xl" style={{ backgroundColor: EARTH.sage[50] || '#f0f5f0', border: `1px solid ${EARTH.sage[200] || '#d0e0d0'}` }}>
                        <div className="text-xs font-bold mb-2 flex items-center gap-1.5" style={{ color: EARTH.sage[700] || '#4a6a4a', fontFamily: TYPOGRAPHY.display, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          <Activity className="w-3 h-3" /> Mix Techniques
                        </div>
                        {recreationGuide.mixTechniques.map((tip, i) => (
                          <p key={i} className="text-xs mb-1.5" style={{ color: EARTH.stone[600] }}>• {tip}</p>
                        ))}
                      </div>

                      {/* Arrangement Philosophy */}
                      <div className="p-3 rounded-xl" style={{ backgroundColor: EARTH.terracotta[50] || '#fdf5f0', border: `1px solid ${EARTH.terracotta[200] || '#e8c8b0'}` }}>
                        <div className="text-xs font-bold mb-2 flex items-center gap-1.5" style={{ color: EARTH.terracotta[700] || '#8a4a2a', fontFamily: TYPOGRAPHY.display, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          <Layout className="w-3 h-3" /> Arrangement Philosophy
                        </div>
                        {recreationGuide.arrangement.map((tip, i) => (
                          <p key={i} className="text-xs mb-1.5" style={{ color: EARTH.stone[600] }}>• {tip}</p>
                        ))}
                      </div>

                      {/* Section-by-Section Tips */}
                      <div className="p-3 rounded-xl" style={{ backgroundColor: '#fff', border: `1px solid ${EARTH.stone[200]}` }}>
                        <div className="text-xs font-bold mb-2 flex items-center gap-1.5" style={{ color: EARTH.stone[700], fontFamily: TYPOGRAPHY.display, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          <FileText className="w-3 h-3" /> Section-by-Section
                        </div>
                        {recreationGuide.sectionTips.map((s, i) => (
                          <div key={i} className="mb-2 last:mb-0">
                            <div className="text-xs font-semibold" style={{ color: EARTH.stone[800] }}>{s.label} <span style={{ color: EARTH.stone[400] }}>({s.bars} bars)</span></div>
                            {s.tips.map((tip, j) => (
                              <p key={j} className="text-xs ml-3" style={{ color: EARTH.stone[500] }}>→ {tip}</p>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Apply button */}
              <button
                onClick={handleApply}
                className="w-full py-3 rounded-xl font-bold text-sm transition-all neo-shadow"
                style={{ backgroundColor: EARTH.olive[600], color: '#fff', fontFamily: TYPOGRAPHY.display, letterSpacing: '0.5px', textTransform: 'uppercase' }}
              >
                BUILD THIS STRUCTURE ON CANVAS
              </button>
            </div>
          )}

          {/* Empty state */}
          {!searchQuery && searchResults.length === 0 && !analysisResult && (
            <div className="text-center py-12">
              <Search className="w-10 h-10 mx-auto mb-3" style={{ color: EARTH.stone[300] }} />
              <p className="text-sm font-medium" style={{ color: EARTH.stone[600] }}>Search for any published track</p>
              <p className="text-xs mt-1" style={{ color: EARTH.stone[400] }}>We'll reverse engineer its arrangement structure so you can learn from it or use it as a starting point</p>
            </div>
          )}

          {isAnalyzing && (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin mx-auto mb-3" style={{ borderColor: EARTH.olive[400], borderTopColor: 'transparent' }} />
              <p className="text-sm" style={{ color: EARTH.stone[500] }}>Analyzing structure...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { TrackAnalyzerDialog };
